import { getCustomer } from 'api/ovation/customers'
import ChilipiperSection from 'components/ChilipiperSection'
import NotificationBanner from 'components/NotificationBanner/NotificationBanner'
import SingleLocationDropdown from 'components/SingleLocationDropdown/SingleLocationDropdown'
import LoadingSkeleton from 'components/LoadingSkeleton/LoadingSkeleton'
import { useGetLocationPartners, useGetLocations } from 'hooks'
import useIsMobile from 'hooks/useIsMobile'
import React, { useCallback, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Location } from 'types'
import ConversationChat from './ConversationsList/ConversationChat/ConversationChat'
import ConversationsList from './ConversationsList/ConversationsList'
import CustomerInfoSection from './CustomerInfoSection/CustomerInfoSection'
import {
  ChilipiperSectionWrapper,
  CustomerSurveyContainer,
  GridContainer,
  LeftGridSection,
  LocationDropdownWrapper,
  MiddleGridSection,
  RightGridSection,
} from './styles'
import { Grid, Typography, Box } from '@mui/material'

const CustomerSurveys: React.FC = () => {
  const { isMobile } = useIsMobile()
  const { defaultLocation, setDefaultLocation, locations, findAndSetDefaultLocation } =
    useGetLocations()
  const [searchParams, setSearchParams] = useSearchParams()

  // Get the partner ID from URL or default location
  const partnerId = searchParams.get('partner') || defaultLocation?.id?.toString() || ''
  const { partner, organization, isLoading: partnerLoading } = useGetLocationPartners(partnerId)

  const [selectedConversation, setSelectedConversation] = useState<any | null>(null)
  const [selectedCustomer, setSelectedCustomer] = useState<any | null>(null)
  const [showCustomerInfo, setShowCustomerInfo] = useState(false)
  const [isExpanded, setIsExpanded] = useState(false)
  const [contentReady, setContentReady] = useState(false)

  const isPlatformOrganization = organization?.attributes?.chowly_platform ?? false
  const organizationOnboardingIncomplete =
    organization?.attributes?.ovation_onboarding_started &&
    !organization?.attributes?.ovation_phone_verified
  const partnerOnboardingIncomplete =
    partner?.attributes?.ovation_onboarding_started && !partner?.attributes?.ovation_phone_verified
  const showVerificationBanner = organizationOnboardingIncomplete || partnerOnboardingIncomplete
  const isOnboarded =
    organization?.attributes?.ovation_phone_verified && partner?.attributes?.ovation_phone_verified

  // Handle direct URL access
  useEffect(() => {
    const urlPartnerId = searchParams.get('partner')
    if (urlPartnerId) {
      const updateURL = (id: string) => `/customer-surveys?partner=${id}`
      findAndSetDefaultLocation(urlPartnerId, updateURL)
    }
  }, [])

  // Update location based on URL parameter
  useEffect(() => {
    const urlPartnerId = searchParams.get('partner')

    if (urlPartnerId && locations.length > 0) {
      const locationFromUrl = locations.find((loc) => loc.id.toString() === urlPartnerId)
      if (locationFromUrl) {
        const locationData = {
          id: locationFromUrl.id.toString(),
          name: locationFromUrl.name,
          organization_id: locationFromUrl.organization_id,
          organization_name: locationFromUrl.organization_name,
          timezone: locationFromUrl.timezone,
        }
        setDefaultLocation(locationData)
      }
    } else if (!urlPartnerId && defaultLocation?.id) {
      setSearchParams({ partner: defaultLocation.id.toString() })
    }
  }, [locations, defaultLocation?.id, searchParams])

  useEffect(() => {
    if (partnerId && partner && organization) {
      setContentReady(true)
    }
  }, [partnerId, partner, organization])

  const handleBackToList = () => {
    setSelectedConversation(null)
    setShowCustomerInfo(false)
  }

  const handleBackToChat = () => {
    setShowCustomerInfo(false)
  }

  const handleShowCustomerInfo = () => {
    setShowCustomerInfo(true)
  }

  // Handle location change from the dropdown
  const handleLocationChange = useCallback(
    (location: Location) => {
      setDefaultLocation(location)
      setSearchParams({ partner: location.id })
      setContentReady(false)
    },
    [setDefaultLocation, setSearchParams],
  )

  const handleExpandChat = (expanded: boolean) => {
    setIsExpanded(expanded)
  }

  const getPhoneVerificationStatus = () => {
    const phone = partner?.attributes?.ovation_phone
    const verified = partner?.attributes?.ovation_phone_verified

    if (!phone) return 1
    return verified ? 3 : 2
  }

  const mapStatusesToColorValues = {
    1: '#FF0000', // Not Verified
    2: '#FF9800', // Pending Verification
    3: '#4CAF50', // Verified
  }

  const mapStatusesNames = {
    1: 'Not Verified',
    2: 'Pending Verification',
    3: 'Verified',
  }

  const phoneStatus = getPhoneVerificationStatus();

  useEffect(() => {
    if (selectedConversation?.customer?._id && partnerId) {
      getCustomer(selectedConversation.customer._id, partnerId).then((customer) => {
        setSelectedCustomer(customer)
      })
    }
  }, [selectedConversation, partnerId])

  useEffect(() => {
    setSelectedConversation(null)
    setSelectedCustomer(null)
    setShowCustomerInfo(false)
  }, [partner])

  const LoadingSkeletonWrapper = () => (
    <Box sx={{ width: '100%' }}>
      <LoadingSkeleton height={40} width="100%" />
      <Box sx={{ mt: 3, mb: 4 }}>
        <LoadingSkeleton height={30} width="60%" />
      </Box>
      <Grid container spacing={2}>
        <Grid item xs={12} md={3}>
          <LoadingSkeleton height={400} />
        </Grid>
        <Grid item xs={12} md={6}>
          <LoadingSkeleton height={400} />
        </Grid>
        <Grid item xs={12} md={3}>
          <LoadingSkeleton height={400} />
        </Grid>
      </Grid>
    </Box>
  )

  return (
    <CustomerSurveyContainer>
      <LocationDropdownWrapper>
        <SingleLocationDropdown onLocationChange={handleLocationChange} />
      </LocationDropdownWrapper>
      
      {!contentReady || partnerLoading ? (
        <LoadingSkeletonWrapper />
      ) : !isPlatformOrganization ? (
        <ChilipiperSectionWrapper>
          <ChilipiperSection
            message='The selected Location is not a Platform location.'
            actionBtnText='Reach out to sales to learn how you can upgrade to a Platform Plan'
          />
        </ChilipiperSectionWrapper>
      ) : (
        <>
          {showVerificationBanner && (
            <NotificationBanner
              type='warning'
              message='This location is pending toll-free phone number verification. Please contact your Chowly Support rep for more information.'
              icon
              width='100%'
              margin='16px 0'
            />
          )}
          <Grid container direction={'column'} spacing={2} padding={2}>
            <Grid xs={12} md={6} lg={6} xl={6}>
              <Typography
                variant='body1'
              >
                {`Toll Free Number: ${partner?.attributes?.ovation_phone || 'N/A'}`}
              </Typography>
            </Grid>
            <Grid xs={12} md={6} lg={6} xl={6} direction={'row'} display='flex'>
              <Typography
                variant='body1'
              >
                {`Verification Status: `}
                <span style={{ color: mapStatusesToColorValues[phoneStatus] }} >{mapStatusesNames[phoneStatus]}</span>
              </Typography>
            </Grid>
          </Grid>
          <GridContainer>
            <LeftGridSection md={3} $showOnMobile={!selectedConversation}>
              <ConversationsList
                isOnboarded={isOnboarded}
                setSelectedConversation={setSelectedConversation}
              />
            </LeftGridSection>

            <MiddleGridSection
              $showOnMobile={!!selectedConversation && !showCustomerInfo}
              $expanded={isExpanded}
            >
              <ConversationChat
                conversation={selectedConversation}
                onBack={handleBackToList}
                onCustomerInfoClick={handleShowCustomerInfo}
                onExpand={handleExpandChat}
                isExpanded={isExpanded}
              />
            </MiddleGridSection>

            <RightGridSection md={3} $showOnMobile={showCustomerInfo}>
              {(!isExpanded || isMobile) && (
                <CustomerInfoSection customerInfo={selectedCustomer} onBack={handleBackToChat} />
              )}
            </RightGridSection>
          </GridContainer>
        </>
      )}
    </CustomerSurveyContainer>
  )
}

export default CustomerSurveys
